import Vue from "vue";
import _ from "lodash";
import moment from "moment";
import {
    FetchState,
    InvestmentDailyBalanceState,
    InvestmentBalancesResponse,
    InvestmentDailyBalanceRecord,
    FetchStateKey,
    InvestmentCustodian,
    CurrentInvestmentBalances,
    InvestmentDailyBalanceRequest,
    CurrentInvestmentBalance
} from "@/shared/types";
import {
    fetchStateKeyMatch
} from "@/shared/fetchStateHelper";
import * as storeHelper from "@/shared/storeHelper";
import * as bankService from "@/shared/api/bankService";
import * as drivewealthService from "@/shared/api/drivewealthService";
import { defineStore } from "pinia";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useInvestmentThresholdStore } from "@/shared/store/investmentThreshold";
import { templatedFetchKeyStore } from "@/shared/store/fetchKeyedPlugin";
import { useHsabInvestmentStore } from "@/shared/store/hsabInvestment";

const useBalancesByCustodianSubStore = defineStore("investmentDailyBalance_balancesByCustodian", () => {
    const emptyBalancesByCustodian = () => {
        return {
            fetchState: FetchState.Uninitialized,
            fetchKey: null,
            tda: null,
            drivewealth: null,
            umb: null
        };
    };
    return templatedFetchKeyStore<CurrentInvestmentBalances, any>(
        emptyBalancesByCustodian,
        null,
        bankService.getInvestmentBalances,
    );
});


export const initialState: InvestmentDailyBalanceState = {
    records: [],
    dailyBalances: [],
    dailyBalancesDictionary: {},
    balanceCache: {},
    currentEndDateKey: null,
    mostRecentBalance: {
        fetchState: FetchState.Uninitialized,
        fetchKey: null,
        asOfDate: null,
        amount: 0,
        cashAmount: 0,
        investedAmount: 0
    },
    balanceOnFile: {
        fetchState: FetchState.Uninitialized,
        fetchKey: null,
        asOfDate: null,
        amount: 0,
        custodian: InvestmentCustodian.None,
        requestDates: null
    },
    hasDailyBalances: false,
};

function emptyBalanceObject (): CurrentInvestmentBalance {
    return {
        balance: 0,
        date: moment().format("YYYY-MM-DD"),
        cashPositionBalance: 0,
        investedBalance: 0
    };
}

export const useInvestmentDailyBalanceStore = defineStore("investmentDailyBalance", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        balancesByCustodian: () => useBalancesByCustodianSubStore(),
        hasInvestmentAccount: () => !!useBalancesByCustodianSubStore().drivewealth ||
            !!useBalancesByCustodianSubStore().tda ||
            !!useBalancesByCustodianSubStore().umb,

        investmentDataIsLoaded: state => {
            return state.balanceOnFile.fetchState === FetchState.Loaded
                && useBalancesByCustodianSubStore().fetchState === FetchState.Loaded;
        },
        currentBalanceIsLoaded: () => {
            return useBalancesByCustodianSubStore().fetchState === FetchState.Loaded;
        },
        balancesForCurrentDates: state =>
            ((state.dailyBalancesDictionary && state.dailyBalancesDictionary[state.currentEndDateKey]) ?
                state.dailyBalancesDictionary[state.currentEndDateKey] : []),

        tdaCurrentBalance: () => useBalancesByCustodianSubStore().tda || emptyBalanceObject(),
        drivewealthCurrentBalance () {
            // Current DW balance from DW api
            return this.drivewealthCurrentBalanceV2 ||
                // Historical DW balance from daily balances
                this.balancesByCustodian.drivewealth ||
                // No Balance
                emptyBalanceObject();
        },
        drivewealthCurrentBalanceV2 () {
            return this.drivewealthStore.drivewealthAllAccountBalance;
        },
        umbCurrentBalanceOrNull: () => useBalancesByCustodianSubStore().umb,
        hsaBankCurrentBalance: () => {
            const hsabInvestmentStore = useHsabInvestmentStore();
            return (hsabInvestmentStore.investmentAccountStatusLoaded && hsabInvestmentStore.hsabInvestmentBalance) || 0;
        },
        singleCurrentBalance () {
            return useBalancesByCustodianSubStore().drivewealth ||
            useBalancesByCustodianSubStore().tda ||
            useBalancesByCustodianSubStore().umb ||
            this.hsaBankCurrentBalance;
        },
        singleOrDefaultCurrentBalance () {
            return this.singleCurrentBalance || emptyBalanceObject();
        },
        sumOfAllInvestmentBalances () {
            return (this.umbCurrentBalanceOrNull
                    ? this.umbCurrentBalanceOrNull.balance : 0) +
                (this.tdaCurrentBalance ? this.tdaCurrentBalance.balance : 0) +
                (this.drivewealthCurrentBalance ? this.drivewealthCurrentBalance.balance : 0) +
                this.hsaBankCurrentBalance;
        },
        sumOfAllInvestmentBalancesV2 () {
            return (this.umbCurrentBalanceOrNull
                    ? this.umbCurrentBalanceOrNull.balance : 0) +
                (this.tdaCurrentBalance ? this.tdaCurrentBalance.balance : 0) +
                (this.drivewealthCurrentBalanceV2) +
                this.hsaBankCurrentBalance;
        },
        mostRecentInvestmentBalance: state => state.mostRecentBalance,
        investmentBalanceOnFileLoaded: state => {
            return state.balanceOnFile.fetchState === FetchState.Loaded;
        },
        investmentBalanceOnFileAmount: state => {
            if (state.balanceOnFile.fetchState === FetchState.Loaded) {
                return state.balanceOnFile.amount;
            }
            return false;
        },
        investmentBalanceOnFileDate: state => {
            if (state.balanceOnFile.fetchState === FetchState.Loaded) {
                return state.balanceOnFile.asOfDate;
            }
            return false;
        },
        drivewealthStore () {
            return useDrivewealthStore();
        },
        investmentThresholdStore () {
            return useInvestmentThresholdStore();
        },
        hsaDailyBalanceStore () {
            return useHsaDailyBalanceStore();
        },
        isHsaBalanceAndThresholdLoaded () {
            return this.investmentThresholdStore.thresholdsLoaded &&
                (this.hsaDailyBalanceStore.getInstantBalance.fetchState === FetchState.Loaded ||
                    this.hsaDailyBalanceStore.balanceOnFile.fetchState === FetchState.Loaded);
        },
        hsaInvestmentThreshold () {
            if (!this.investmentThresholdStore.thresholdsLoaded) {
                return 0;
            }

            return this.investmentThresholdStore.investmentThreshold(this.hasInvestmentAccount);
        },
        hsaBalanceBeyondThreshold () {
            if (!this.isHsaBalanceAndThresholdLoaded) {
                return 0;
            }
            const hsaBalance = this.hsaDailyBalanceStore.getInstantBalance.amount ||
                this.hsaDailyBalanceStore.balanceOnFile.amount;
            return Math.max(0, hsaBalance - this.hsaInvestmentThreshold);
        },

        hsaBalance () {
            if (!this.isHsaBalanceAndThresholdLoaded) {
                return 0;
            }
            return this.hsaDailyBalanceStore.getInstantBalance.amount || this.hsaDailyBalanceStore.balanceOnFile.amount;
        },
        hasEnoughToOpenAccount () {
            if (!this.isHsaBalanceAndThresholdLoaded) {
                return false;
            }
            return this.hsaDailyBalanceStore.getInstantBalance.amount
            >= this.investmentThresholdStore.thresholdForAccountCreation;
        },

        areBalancesByCustodianLoaded: () => useBalancesByCustodianSubStore().fetchState === FetchState.Loaded,
        hasTdaAccount: () => !!useBalancesByCustodianSubStore().tda,
    },
    actions: {
        setBalancesStateLoading (key: FetchStateKey) {
            this.balanceOnFile.fetchState = FetchState.Loading;
            this.balanceOnFile.fetchKey = key;
        },
        invalidateBalancesFetchState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.balanceOnFile.fetchKey)) {
                return;
            }
            this.records = [];
            this.dailyBalances = [];
            this.dailyBalancesDictionary = {};
            this.balanceOnFile = {
                fetchState: FetchState.Uninitialized,
                fetchKey: null,
                asOfDate: null,
                amount: 0,
                custodian: InvestmentCustodian.None,
            };
        },
        setCurrentBalanceStateLoading (key: FetchStateKey) {
            this.mostRecentBalance.fetchState = FetchState.Loading;
            this.mostRecentBalance.fetchKey = key;
        },
        setCurrentBalanceStateNotLoaded () {
            this.mostRecentBalance.fetchState = FetchState.Uninitialized;
        },
        invalidateCurrentBalanceFetchState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.mostRecentBalance.fetchKey)) {
                return;
            }

            this.mostRecentBalance = {
                fetchState: FetchState.Uninitialized,
                fetchKey: null,
                asOfDate: null,
                amount: 0,
                cashAmount: 0,
                investedAmount: 0
            };
        },
        setDailyBalancesState (
            mutationArguments: {
                fetchKey: FetchStateKey,
                data: InvestmentBalancesResponse,
                custodian: InvestmentCustodian,
                requestDates: {startDate: string, endDate: string}
            }) {


            if (this.balanceOnFile.fetchState !== FetchState.Loading ||
                !fetchStateKeyMatch(this.balanceOnFile.fetchKey, mutationArguments.fetchKey)) {
                return;
            }

            const data = mutationArguments.data;
            const endDateKey = this.currentEndDateKey;
            const records = _.sortBy(data.records.map(x => {
                const momentInst = moment(x.date);
                return {
                    ...x,
                    timestamp: momentInst.unix(),
                    date: momentInst.format("DD-MMM-YY")
                };
            }), [ "timestamp" ]);

            this.dailyBalances = [ ...this.dailyBalances, ...records ];
            Vue.set(this.dailyBalancesDictionary, `${this.currentEndDateKey}`, records);
            this.hasDailyBalances = data.hasBalances;

            const todayKey = moment().format("YYYYMMDD");
            const yesterdayKey = moment().add(-1, "days").format("YYYYMMDD");
            const defaultBalance = { balance: 0, date: moment().add(-1, "day").format("YYYYMMDD") };
            if (endDateKey === todayKey || endDateKey === yesterdayKey) {
                const newestDailyBalance = _.last(records) || defaultBalance;
                this.balanceOnFile = {
                    ...this.balanceOnFile,
                    fetchState: FetchState.Loaded,
                    asOfDate: newestDailyBalance.date,
                    amount: newestDailyBalance.balance,
                    custodian: mutationArguments.custodian,
                    requestDates: mutationArguments.requestDates
                };
            }
            this.balanceOnFile.fetchState = FetchState.Loaded;
        },

        setMostRecentBalance (mutationArguments: {
            fetchKey: FetchStateKey,
            data: InvestmentDailyBalanceRecord
        }) {

            if (this.mostRecentBalance.fetchState !== FetchState.Loading ||
                !fetchStateKeyMatch(this.mostRecentBalance.fetchKey, mutationArguments.fetchKey)) {
                return;
            }

            this.mostRecentBalance = {
                fetchState: FetchState.Loaded,
                fetchKey: mutationArguments.fetchKey,
                amount: mutationArguments.data.balance,
                cashAmount: mutationArguments.data.cashPositionBalance,
                investedAmount: mutationArguments.data.investedBalance,
                asOfDate: mutationArguments.data.date
            };
        },
        setCurrentEndDateKey (currentEndDateKey: string): void {
            this.currentEndDateKey = currentEndDateKey;
        },
        setHsaBankBalance (balance: CurrentInvestmentBalance) {
            this.balancesByCustodian.hsaBank = balance;
        },

        loadDailyInvestmentBalances (params: InvestmentDailyBalanceRequest): Promise<any> {
            const args: {
                startDate: string,
                endDate: string,
                investmentCustodian?: InvestmentCustodian
            } =
                _.pick(params, [ "startDate", "endDate", "investmentCustodian" ]);

            const fetchKey: FetchStateKey = {
                username: params.username,
                partnerId: params.partnerId,
                accountId: params.accountId
            };

            if (this.balanceOnFile.fetchState !== FetchState.Uninitialized &&
                this.balanceOnFile.custodian === params.investmentCustodian &&
                this.balanceOnFile.requestDates &&
                this.balanceOnFile.requestDates.startDate === args.startDate &&
                this.balanceOnFile.requestDates.endDate === args.endDate &&
                fetchStateKeyMatch(this.balanceOnFile.fetchKey, fetchKey)
            ) {
                return Promise.resolve();
            }
            this.setBalancesStateLoading(fetchKey);

            const cacheKey = JSON.stringify(params);
            const cachedData = this.balanceCache[cacheKey];
            if (cachedData) {
                this.setDailyBalancesState(
                    { fetchKey,
                        data: cachedData,
                        custodian: params.investmentCustodian,
                        requestDates: {
                            startDate: args.startDate,
                            endDate: args.endDate }});
                return Promise.resolve();
            }

            if (params.investmentCustodian === InvestmentCustodian.Drivewealth && params.accountId) {
                return drivewealthService.getInvestmentDailyBalances(
                    params.username,
                    params.partnerId,
                    params.accountId,
                    args)
                .then(response => {
                    this.balanceCache[cacheKey] = response.data;
                    this.setDailyBalancesState(
                        { fetchKey,
                            data: response.data,
                            custodian: params.investmentCustodian,
                            requestDates: {
                                startDate: args.startDate,
                                endDate: args.endDate }});
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: false });
                });
            }
            else {
                return bankService.getInvestmentDailyBalances(
                    params.username,
                    params.partnerId,
                    args)
                .then(response => {
                    this.balanceCache[cacheKey] = response.data;
                    this.setDailyBalancesState(
                        { fetchKey,
                            data: response.data,
                            custodian: params.investmentCustodian,
                            requestDates: {
                                startDate: args.startDate,
                                endDate: args.endDate }});
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: false });
                });
            }
        },

        loadCurrentDailyBalance (args?: {
            username?: string,
            partnerId?: number
        }): Promise<any> {
            args = args || {};
            const fetchKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            return Promise.all([
                useBalancesByCustodianSubStore().fetchKeyedApiCall(fetchKey)
                    .catch(err => {
                        return storeHelper.handleError({ error: err, rethrow: false });
                    }),
            ]);
        },

        loadHsaBalanceAndThreshold (
            { force, username, partnerId }: {
                force?: boolean,
                username?: string,
                partnerId?: number
            }
        ) {
            force = !!force;

            return Promise.all([
                    this.hsaDailyBalanceStore.loadInstantBalance({ force, username, partnerId }),
                    this.hsaDailyBalanceStore.loadBalanceOnFile({ username, partnerId }),
                    this.investmentThresholdStore.loadInvestmentThresholds({ username, partnerId }),
                    this.loadCurrentDailyBalance({ username, partnerId })
                ])
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: false });
                });
        }
    }
});
